import type { ComponentType } from 'react';
import {
  Activity,
  ArrowLeftRight,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileArchive,
  Eye,
  FileText,
  Folder,
  Gauge,
  Grid2x2,
  KeyRound,
  LogOut,
  Mail,
  Maximize2,
  Minimize2,
  MonitorSmartphone,
  Pencil,
  Plus,
  Receipt,
  ScrollText,
  Search,
  Settings,
  Shield,
  Trash2,
  Upload,
  User as UserGlyph,
  Users,
  X,
  Copy as CopyGlyph,
  Scissors as ScissorsGlyph,
  ClipboardPaste,
  type LucideProps,
} from 'lucide-react';

/**
 * The icon set.
 *
 * Drawn from Lucide, installed as a dependency rather than pulled from a CDN:
 * this system is meant to run on a VPS that may have no outbound internet, and
 * an icon that silently fails to load is worse than no icon at all. It is ISC
 * licensed, so shipping it inside the product is unencumbered.
 *
 * Every icon is wrapped rather than re-exported so the whole set shares one
 * stroke weight and default size. Mixing weights is the thing that makes an
 * interface look assembled from parts.
 */

const defaults: LucideProps = {
  size: 18,
  strokeWidth: 1.75,
  absoluteStrokeWidth: true,
  'aria-hidden': true,
};

type Glyph = ComponentType<LucideProps>;

const wrap =
  (Component: Glyph): Glyph =>
  (props) => <Component {...defaults} {...props} />;

export const Icons = {
  // Navigation
  Dashboard: wrap(Gauge),
  Folder: wrap(Folder),
  Invoices: wrap(Receipt),
  Reports: wrap(Activity),
  Users: wrap(Users),
  AuditLog: wrap(ScrollText),
  Sessions: wrap(MonitorSmartphone),
  Security: wrap(Shield),
  Profile: wrap(UserGlyph),
  Settings: wrap(Settings),
  Company: wrap(Building2),
  Sheet: wrap(Grid2x2),
  Import: wrap(ArrowLeftRight),
  Mail: wrap(Mail),

  // Actions
  Plus: wrap(Plus),
  Search: wrap(Search),
  Upload: wrap(Upload),
  Download: wrap(Download),
  Archive: wrap(FileArchive),
  Trash: wrap(Trash2),
  Pencil: wrap(Pencil),
  Copy: wrap(CopyGlyph),
  Scissors: wrap(ScissorsGlyph),
  Clipboard: wrap(ClipboardPaste),
  Check: wrap(Check),
  Close: wrap(X),
  Eye: wrap(Eye),
  Key: wrap(KeyRound),
  SignOut: wrap(LogOut),
  Clock: wrap(Clock),
  ChevronRight: wrap(ChevronRight),
  ChevronDown: wrap(ChevronDown),
  Fullscreen: wrap(Maximize2),
  ExitFullscreen: wrap(Minimize2),

  /** Kept for the rows that still ask for a generic document. */
  File: wrap(FileText),
};

// ---------------------------------------------------------------------------
// Supplied artwork
// ---------------------------------------------------------------------------

/**
 * The client's own icons, served from /brand.
 *
 * Used for the two places where a coloured icon carries meaning at a glance:
 * the sidebar, and the type of an uploaded file. Inline controls — a menu
 * item, a toolbar button — stay on the drawn monochrome set, because a
 * full-colour pictogram sitting inside a row of grey glyphs reads as a
 * mistake rather than as emphasis.
 *
 * Raster, so they are given explicit dimensions and never scaled up: these
 * are supplied around 128px and rendered at 18–20, which downsamples
 * cleanly. Anything asking for more than 64 gets the file at its own size
 * rather than a blurry enlargement.
 */
export const BRAND = {
  dashboard: '/brand/dashboard.png',
  drive: '/brand/google-drive.png',
  trash: '/brand/trash.png',
  invoices: '/brand/invoice.png',
  reports: '/brand/report.png',
  import: '/brand/file-import.png',
  users: '/brand/user.png',
  audit: '/brand/logs.png',
  sessions: '/brand/signins.png',
  security: '/brand/security.png',
  signOut: '/brand/signout.png',
  folder: '/brand/folder.png',
  pdf: '/brand/pdf.png',
  word: '/brand/doc.png',
  excel: '/brand/xls.png',
  image: '/brand/anyimage.png',
} as const;

export function BrandIcon({
  src,
  size = 20,
  className,
  alt = '',
}: {
  src: string;
  size?: number;
  className?: string | undefined;
  alt?: string;
}) {
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      className={className}
      style={{ width: size, height: size, objectFit: 'contain' }}
      aria-hidden={alt === '' ? true : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// File types
// ---------------------------------------------------------------------------

/**
 * Icons for the formats a person recognises at a glance.
 *
 * PDF, Word, Excel and images use the artwork supplied in /brand. The rest —
 * PowerPoint, video, audio, archives, plain text and anything unrecognised —
 * have no supplied file, so they keep the drawn versions below. Those follow
 * the same convention deliberately: a green sheet is a spreadsheet, a blue
 * page is a document, a red one is a PDF. The convention belongs to nobody;
 * Microsoft's actual icons are their trademarks and are not ours to ship.
 */

/** Kinds with supplied artwork. Anything absent falls through to a drawing. */
const SUPPLIED: Partial<Record<FileKind, string>> = {
  pdf: BRAND.pdf,
  word: BRAND.word,
  excel: BRAND.excel,
  image: BRAND.image,
};

const FILE_KINDS = {
  excel: { tint: '#137e46', accent: '#0f6b3b', letter: 'X' },
  word: { tint: '#1c5fbe', accent: '#174e9c', letter: 'W' },
  powerpoint: { tint: '#c4441f', accent: '#a53818', letter: 'P' },
  pdf: { tint: '#c8262a', accent: '#a71f22', letter: 'PDF' },
  image: { tint: '#7a4fc0', accent: '#653fa3', letter: '' },
  video: { tint: '#b5651d', accent: '#985417', letter: '' },
  audio: { tint: '#0f7d8c', accent: '#0b6270', letter: '' },
  archive: { tint: '#8a6d1f', accent: '#6f5718', letter: '' },
  text: { tint: '#5a6270', accent: '#474d59', letter: '' },
  generic: { tint: '#7c8493', accent: '#646b78', letter: '' },
} as const;

export type FileKind = keyof typeof FILE_KINDS;

/** Work out which icon a name deserves. Extension only — MIME types lie. */
export function fileKindOf(name: string, mimeType = ''): FileKind {
  const ext = name.toLowerCase().split('.').pop() ?? '';

  if (['xlsx', 'xlsm', 'xlsb', 'xls', 'csv', 'tsv', 'ods'].includes(ext)) return 'excel';
  if (['docx', 'doc', 'rtf', 'odt'].includes(ext)) return 'word';
  if (['pptx', 'ppt', 'odp'].includes(ext)) return 'powerpoint';
  if (ext === 'pdf') return 'pdf';
  if (
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'heic', 'tif', 'tiff'].includes(ext)
  )
    return 'image';
  if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) return 'audio';
  if (['zip', '7z', 'rar', 'tar', 'gz'].includes(ext)) return 'archive';
  if (['txt', 'md', 'log', 'json', 'xml', 'yml', 'yaml'].includes(ext)) return 'text';

  // Fall back to the MIME type only when the name told us nothing.
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('text/')) return 'text';
  if (mimeType === 'application/pdf') return 'pdf';

  return 'generic';
}

export function FileTypeIcon({
  name,
  mimeType,
  size = 20,
  className,
}: {
  name: string;
  mimeType?: string;
  size?: number;
  className?: string;
}) {
  const kind = fileKindOf(name, mimeType);

  const supplied = SUPPLIED[kind];
  if (supplied) {
    return <BrandIcon src={supplied} size={size} className={className} alt={`${kind} file`} />;
  }

  const { tint, accent, letter } = FILE_KINDS[kind];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      role="img"
      aria-label={`${kind} file`}
    >
      {/* Page with a folded corner — the shape everyone reads as "a file". */}
      <path
        d="M5.5 2.75h8.19L19.5 8.4v12.85a1.5 1.5 0 0 1-1.5 1.5H5.5a1.5 1.5 0 0 1-1.5-1.5V4.25a1.5 1.5 0 0 1 1.5-1.5Z"
        fill={tint}
      />
      <path d="M13.69 2.75 19.5 8.4h-4.31a1.5 1.5 0 0 1-1.5-1.5V2.75Z" fill={accent} />

      {letter ? (
        <text
          x="11.6"
          y="17.6"
          textAnchor="middle"
          fill="#ffffff"
          fontFamily="Segoe UI, system-ui, sans-serif"
          fontWeight="700"
          fontSize={letter.length > 1 ? 6 : 9}
        >
          {letter}
        </text>
      ) : (
        <KindMark kind={kind} />
      )}
    </svg>
  );
}

/** A small white pictogram for the types a letter would not describe. */
function KindMark({ kind }: { kind: FileKind }) {
  const stroke = {
    fill: 'none',
    stroke: '#ffffff',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (kind) {
    case 'image':
      return (
        <g {...stroke}>
          <rect x="7" y="11" width="9.5" height="7.5" rx="1" />
          <path d="m7.6 17.4 2.6-2.8 1.9 1.9 1.9-2.3 2.3 3.2" />
          <circle cx="9.6" cy="13.2" r="0.75" fill="#ffffff" stroke="none" />
        </g>
      );
    case 'video':
      return (
        <g {...stroke}>
          <rect x="6.8" y="11.2" width="9.8" height="7" rx="1.4" />
          <path d="m10.6 13.2 3.2 1.5-3.2 1.5z" fill="#ffffff" stroke="none" />
        </g>
      );
    case 'audio':
      return (
        <g {...stroke}>
          <path d="M15.3 11.4v5.1" />
          <path d="M15.3 11.4 10.4 12.5v5.1" />
          <circle cx="9" cy="17.6" r="1.5" fill="#ffffff" stroke="none" />
          <circle cx="14" cy="16.5" r="1.5" fill="#ffffff" stroke="none" />
        </g>
      );
    case 'archive':
      return (
        <g {...stroke}>
          <path d="M11.6 10.6v1.6M11.6 13.4v1.6M11.6 16.2v1.1" />
          <rect x="10.2" y="17.2" width="2.8" height="2.4" rx="0.7" />
        </g>
      );
    default:
      return (
        <g {...stroke}>
          <path d="M7.4 12.4h8.4M7.4 15h8.4M7.4 17.6h5.2" />
        </g>
      );
  }
}
